import { faCircleMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BoxInput } from "../components/box-input";
import { Button } from "../components/button";
import { Form } from "../components/form";
import { H1 } from "../components/h1";
import { H2 } from "../components/h2";
import { Layout } from "../components/layout";
import { useConnection } from "../stores/connection";
import { useLocale } from "../stores/locale";
import { StoredView, useViews } from "../stores/views";
import { randomCode } from "../utils/random-code";

export function Start() {
  const { locale: t } = useLocale();
  const navigate = useNavigate();
  const { signin } = useConnection();
  const [code, setCode] = useState("");
  const [error, setError] = useState<boolean>(false);
  return (
    <Layout footer={<Footer />}>
      <div className="flex flex-col gap-5 flex-grow justify-center">
        <Form
          className="flex flex-col gap-5 items-center"
          onsubmit={() => {
            (async () => {
              try {
                await signin(code, "user");
                navigate(`/${code}`);
              } catch (error: unknown) {
                setError(true);
                setTimeout(() => {
                  setError(false);
                }, 1000);
              }
            })();
          }}
        >
          <H1>{t.enterCode}</H1>
          <BoxInput length={6} onChange={setCode} />
          <Button
            className={`mx-auto  ${error ? "animate-shake" : ""}`}
            disabled={code.length !== 6}
            type="submit"
          >
            {t.view}
          </Button>
        </Form>
        <RecentViews />
      </div>
    </Layout>
  );
}

function RecentViews() {
  const { locale: t } = useLocale();
  const { views, deleteView } = useViews();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (views.length === 0) {
      setIsEditing(false);
    }
  }, [views.length]);

  if (views.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto mb-8 w-full max-w-xs">
      <div className="mb-3 flex items-center justify-between">
        <H2>{t.recentViews}</H2>
        <button
          className="text-sm text-blue-400"
          type="button"
          onClick={() => {
            setIsEditing((current) => !current);
          }}
        >
          {isEditing ? t.done : t.edit}
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {views.map((view) => (
          <RecentViewItem
            key={view.code}
            deleteLabel={t.delete}
            isEditing={isEditing}
            view={view}
            onDelete={() => {
              deleteView(view.code);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function RecentViewItem({
  deleteLabel,
  isEditing,
  view,
  onDelete,
}: {
  deleteLabel: string;
  isEditing: boolean;
  view: StoredView;
  onDelete: () => void;
}) {
  const content = (
    <div className="flex w-full items-center justify-between">
      <span>{view.name}</span>
      <span className="text-xs text-gray-300">{view.code}</span>
    </div>
  );

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <button
          aria-label={deleteLabel}
          className="flex h-6 w-6 shrink-0 items-center justify-center text-red-500"
          type="button"
          onClick={onDelete}
        >
          <FontAwesomeIcon icon={faCircleMinus} />
        </button>
        <div className="w-full rounded-md bg-gray-500 px-4 py-2 text-base normal-case text-white">
          {content}
        </div>
      </div>
    );
  }

  return (
    <Button
      className="w-full !normal-case flex justify-between"
      href={`/${view.code}`}
      severity="secondary"
      type="route"
    >
      {content}
    </Button>
  );
}

function Footer() {
  const { locale: t } = useLocale();
  const navigate = useNavigate();
  const { signup, signin } = useConnection();

  const [loading, setLoading] = useState(false);

  return (
    <Form
      onsubmit={() => {
        (async () => {
          setLoading(true);
          const code = randomCode();
          try {
            await signup(code);
            await signin(code, "admin");
            navigate(`/${code}/edit`);
          } catch (error: unknown) {
            console.error(error);
            alert("Failed to create view, please try again.");
          } finally {
            setLoading(false);
          }
        })();
      }}
    >
      <Button loading={loading} severity="secondary" size="sm" type="submit">
        {t.create}
      </Button>
    </Form>
  );
}
